import React from 'react';
import styled from 'styled-components';
import { lighten } from 'polished';
import { t } from 'ttag';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Popover, Typography, Tag as AntTag, Space } from 'antd';

const POPUP_LIMIT = 7;
const ITEMS_MAX = 3;
const { Text, Paragraph } = Typography;

interface Item {
  id: string | number;
  name: string;
  color?: string;
}

const Tag = styled(AntTag)<{ hue: string; $uppercase: boolean }>`
  border-color: ${props => props.hue && lighten(0.05, props.hue)};
  border-style: solid;
  color: ${props => props.hue};
  text-transform: ${props => (props.$uppercase ? 'uppercase' : 'none')};
`;

const PopupContent = styled.div`
  max-width: 300px;
  && .ant-tag {
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 300px;
  }
`;

const getTags = ({ items, uppercase }: { items: Item[]; uppercase: boolean }) =>
  items.map(({ id, color, name }) => (
    <Tag style={{ marginRight: 0 }} key={id} hue={color} $uppercase={uppercase}>
      {name}
    </Tag>
  ));

const itemsPopup = (items: Item[], isTag: boolean, uppercase: boolean) => {
  const moreCount = items.length - POPUP_LIMIT;
  const displayItems = items.slice(0, POPUP_LIMIT);
  return (
    <PopupContent>
      <Space size={8} wrap>
        {isTag && getTags({ items: displayItems, uppercase })}
        {!isTag && displayItems.map(({ id, name }) => <Paragraph key={id}>{name}</Paragraph>)}
        {moreCount > 0 && <Paragraph type="secondary">{t`+ ${moreCount} more`}</Paragraph>}
      </Space>
    </PopupContent>
  );
};

interface Props {
  items: Item[];
  isTag?: boolean;
  uppercase?: boolean;
}

const ShowMore = ({ items, isTag = false, uppercase = true }: Props) => {
  const displayItems = items.slice(0, ITEMS_MAX);
  return (
    <Space size={8} wrap>
      {isTag && getTags({ items: displayItems, uppercase })}
      {!isTag && (
        <Text style={{ marginRight: 8 }}>{displayItems.map(({ name }) => name).join(', ')}</Text>
      )}
      {items.length > ITEMS_MAX && (
        <Popover content={itemsPopup(items, isTag, uppercase)}>
          <Text>
            +{items.length - ITEMS_MAX}
            <InfoCircleOutlined style={{ marginLeft: 8 }} />
          </Text>
        </Popover>
      )}
    </Space>
  );
};

export default ShowMore;
